import { defineStore, storeToRefs } from 'pinia'
import { useQueueStore } from '../queueStore'
import { useMediaFileStore } from '../mediaFileStore'
import { usePlayerStore } from '../player/karaokePlayer'
import { useUserStore } from '../userStore'
import { useLyrics } from '../../composables/useLyrics'
import { useNuxtApp, useRuntimeConfig } from '#imports'

export const useSecondScreenStore = defineStore('secondScreenStore', () => {
	const popoutWindow = ref<Window | null>(null)
	const playerState = ref<string | null>(null)
	const showSecondScreenGuidance = ref(false)
	const loadingPlayerData = ref(false)

	const hasConnection = computed(() => popoutWindow.value !== null && popoutWindow.value !== undefined)
	const isPlayerPlaying = computed(() => (playerState.value && playerState.value === 'resume') || playerState.value === 'started')
	const isPlayerInactive = computed(() => playerState.value !== null && (playerState.value === 'interrupted' || playerState.value === 'finished'))
	const isPlayerLoading = computed(() => playerState.value !== null && playerState.value === 'loading')
	const isSecondScreenActive = computed(() => popoutWindow.value !== null)

	const { $i18n } = useNuxtApp()

	const karaokePlayerStore = usePlayerStore()
	const queueStore = useQueueStore()
	const mediaFileStore = useMediaFileStore()
	const userStore = useUserStore()

	const { images, lyrics, audioFileByType } = storeToRefs(mediaFileStore)
	const { firstInQueue, countdownTime } = storeToRefs(queueStore)
	const { outroStart } = useLyrics()
	// https://github.com/nuxt-modules/i18n/issues/3451#issuecomment-2746370390
	const { t } = $i18n
	const { config } = useRuntimeConfig()

	const bringPopoutWindowToFront = () => {
		if (popoutWindow.value) {
			popoutWindow.value.focus()
		}
	}

	const closePopoutWindow = () => {
		const { setLoadingState } = karaokePlayerStore
		const { $audioPlayer } = useNuxtApp()

		popoutWindow.value = null

		$audioPlayer.pause()
		setLoadingState(false)
	}

	const sendMessageToSecondScreen = async (data: any) => {
		const dataToSend = JSON.stringify(data)
		try {
			if (popoutWindow.value) {
				const currentPopout = popoutWindow.value
				if (currentPopout && !currentPopout.closed) {
					currentPopout.postMessage(dataToSend, '*')
				} else {
					console.warn('Popout window is closed or unavailable, cannot send message.')
					closePopoutWindow()
				}
			}
		} catch (error) {
			console.error('error sending message', error)
		}
	}

	const startPlayerInSecondScreen = (countdown = false) => {
		const { $audioPlayer } = useNuxtApp()
		if (!firstInQueue.value) {
			console.error('startPlayerInSecondScreen called but firstInQueue is null. Aborting.')
			return
		}
		if (!lyrics.value?.content) {
			console.error('startPlayerInSecondScreen called but lyrics.content is null. Aborting.')
			return
		}
		firstInQueue.value.variant.outroStart = outroStart(lyrics.value.content)

		sendMessageToSecondScreen({
			method: 'START_PLAYER',
			dataForPlayer: {
				images: toRaw(images.value.content),
				audio: toRaw(audioFileByType.value),
				lyrics: toRaw(lyrics.value.content),
				song: toRaw(firstInQueue.value)
			},
			showLoading: true,
			startingTime: $audioPlayer.getCurrentTime()
		})
		if (countdown) {
			sendMessageToSecondScreen({ method: 'SET_AUTOPLAY_COUNTDOWN', initialCountdown: countdownTime.value })
		}
	}

	const setupConnection = ({ popoutWindowParam }: { popoutWindowParam: Window }) => {
		const handleEvent = (event: MessageEvent) => {
			const { $audioPlayer } = useNuxtApp()
			const { setLoadingState } = karaokePlayerStore

			if (event.source !== popoutWindow.value) {
				return
			}

			try {
				const data = (typeof event.data === 'string') ? JSON.parse(event.data) : event.data

				if (data.method === 'SECOND_SCREEN_PLAYER_EVENT') {
					// @ts-ignore
					useEventEmit(`playerEvent:${data.playerEvent}`, true)
					playerState.value = data.playerEvent

					if (data.playerEvent === 'finish') {
						queueStore.removeCurrentFromQueue()
					} else if (data.playerEvent === 'ready') {
						loadingPlayerData.value = false
						window.audioPlayer.events.addEventListener('frequenttimeupdate', (timeStamp: any) => {
							sendMessageToSecondScreen({ method: 'SET_PLAYER_TIME_STAMP', timeStamp })
						}, 'PLAYER_CONTROLS_LISTENER')
						if (firstInQueue.value && (userStore.queueAutoPlay || firstInQueue.value.bypassAutoPlay)) {
							$audioPlayer.play()
						}
						setLoadingState(false)
					} else if (data.playerEvent === 'interrupted') {
						queueStore.rewindSong()
					} else if (data.playerEvent === 'started') {
						loadingPlayerData.value = false
						$audioPlayer.play()
						queueStore.songStarted()
					}
				} else if (data.method === 'SEND_NEXT_UP_DATA') {
					// console.log('setting data', data)
				} else if (data.method === 'SECOND_SCREEN_READY') {
					if (typeof window !== 'undefined') {
						window.focus()
					}

					if (queueStore.hasSongFiles && firstInQueue.value && lyrics.value?.content) {
						startPlayerInSecondScreen()
					} else {
						console.warn('Second screen ready, but no song in queue, firstInQueue is null, or lyrics not loaded yet.')
					}
					const timeout = queueStore.hasSongFiles ? 5000 : 500
					setTimeout(() => {
						sendMessageToSecondScreen({
							method: 'SET_QUEUE_INFO',
							queueInfo: queueStore.queue,
							hideLoading: true
						})
					}, timeout)
				} else if (data.method === 'POPOUT_UNLOAD') {
					window.removeEventListener('message', handleEvent, false)
					closePopoutWindow()
				}
			} catch (error) {
				console.error('Error handling message in secondScreenStore:', error, event.data)
			}
		}

		if (popoutWindowParam) {
			popoutWindow.value = popoutWindowParam

			window.removeEventListener('message', handleEvent, false)
			window.addEventListener('message', handleEvent)
		}
	}

	const closeConnection = async () => {
		const { $audioPlayer } = useNuxtApp()

		// Send destroy command
		if (popoutWindow.value && !popoutWindow.value.closed) {
			sendMessageToSecondScreen({ method: 'PLAYER_ACTION', action: 'destroy' })

			// Start a timeout to close the window after a delay
			setTimeout(() => {
				$audioPlayer.pause()
				if (popoutWindow.value && !popoutWindow.value.closed) {
					popoutWindow.value.close()
				}
				closePopoutWindow() // Clean up main window state
			}, 300) // 300ms delay
		} else {
			// If no popout window exists or it's already closed, just clean up main state
			$audioPlayer.pause()
			closePopoutWindow()
		}
	}

	const openPopoutWindow = async () => {
		showSecondScreenGuidance.value = true
		// @ts-ignore
		const base = config?.app?.baseURL || ''
		const { $oruga } = useNuxtApp()

		const popoutUrl = `${base}presentation/`
		let openedWindow: Window | null = null

		try {
			openedWindow = window.open(
				popoutUrl,
				'popoutPlayer',
				'width=' + screen.availWidth * 0.6 + ',height=' + screen.availHeight * 0.6 + ',scrollbars=yes,resizable=yes'
			)
		} catch (error) {
			console.error('Error opening popout window:', error)
			showSecondScreenGuidance.value = false
			$oruga.notifications.open({
				message: t('error.popupBlocked'),
				variant: 'warning',
				position: 'top-right',
				duration: 10000
			})
			return Promise.resolve({ popoutOpened: false, error: 'popup_blocked_or_failed' })
		}

		if (openedWindow) {
			showSecondScreenGuidance.value = false
			setupConnection({ popoutWindowParam: openedWindow })

			window.addEventListener('beforeunload', () => {
				if (popoutWindow.value && !popoutWindow.value.closed) {
					popoutWindow.value.close()
				}
			})

			const checkPopoutClosed = setInterval(() => {
				try {
					if (popoutWindow.value === null || (popoutWindow.value && popoutWindow.value.closed)) {
						clearInterval(checkPopoutClosed)
						if (popoutWindow.value) {
							closePopoutWindow()
						} else {
							// It was likely blocked or failed to open, already handled in catch
						}
					}
				} catch (error) {
					console.warn('Error checking popout window status (might be cross-origin):', error)
					clearInterval(checkPopoutClosed)
					closePopoutWindow()
				}
			}, 1000)

			setTimeout(() => {
				clearInterval(checkPopoutClosed)
			}, 15000)

			return Promise.resolve({ popoutOpened: true })
		} else {
			showSecondScreenGuidance.value = false
			console.warn('window.open returned null, possibly blocked.')
			$oruga.notifications.open({
				message: t('error.popupBlocked'),
				variant: 'warning',
				position: 'top-right',
				duration: 10000
			})
			return Promise.resolve({ popoutOpened: false, error: 'popup_blocked' })
		}
	}
	const commandPlayer = (action: any) => {
		sendMessageToSecondScreen({ method: 'PLAYER_ACTION', action })
	}

	return {
		popoutWindow,
		playerState,
		showSecondScreenGuidance,
		loadingPlayerData,
		hasConnection,
		isPlayerPlaying,
		isPlayerInactive,
		isPlayerLoading,
		isSecondScreenActive,
		closeConnection,
		setupConnection,
		startPlayerInSecondScreen,
		sendMessageToSecondScreen,
		openPopoutWindow,
		bringPopoutWindowToFront,
		closePopoutWindow,
		commandPlayer
	}
})
